import { getDatabase, ref, update, push } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { FloorAreaVerification, ValueStatus, ValueWithMetadata } from '../types/protocols';
import { AnyGeometryShape, ComplexShape } from '../types/GeometryShapes';
import { formatDateForStorage } from '../Helpers/DateUtils';
import { validateFloorAreaSplit } from '../Helpers/floorAreaVerification';
import { recordActivity } from './activityApi';

/** One floor component of the room with the area the dialog gave it. */
export interface FloorAreaRowInput {
    componentKey: string;
    /** What the floor's shapes add up to today (null = no shapes). */
    currentArea: number | null;
    newArea: number;
}

export interface SaveRoomFloorAreaInput {
    /** The room's new golvyta; with several floors the rows must add up to it. */
    total: number;
    floors: FloorAreaRowInput[];
    /** Tick "Markera som uppmätt på ritning": write the verification; untick: remove it. */
    verify: boolean;
    comment?: string;
}

/** Below this a row counts as unchanged and its floor keeps its original shapes. */
const UNCHANGED_TOLERANCE = 0.001;

/**
 * Golvyta uppmätt på ritning (web#1383): the one writer of a room's
 * `floorAreaVerification`, and the room-page way of setting floor areas.
 * One root `update()`: every floor whose area changed gets its `areaShapes`
 * replaced by a single `complex` shape (the shape kind that stores an area
 * instead of dimensions, as the maintenance draft's Mängd modal does); floors
 * whose value is unchanged keep their original shapes; the room's
 * verification is written or removed.
 */
export class RoomFloorAreaApi {
    private db = getDatabase();

    buildUpdates(buildingKey: string, roomKey: string, input: SaveRoomFloorAreaInput, uid: string): Record<string, unknown> {
        const error = validateFloorAreaSplit(input.total, input.floors.map(floor => floor.newArea));
        if (error) throw new Error(error);

        const now = formatDateForStorage(new Date());
        const updates: Record<string, unknown> = {};

        for (const floor of input.floors) {
            if (Math.abs(floor.newArea - (floor.currentArea ?? 0)) <= UNCHANGED_TOLERANCE) continue;
            // Firebase auto-ID keeps shape keys consistent with the rest of the schema (iOS uses newKey()).
            const key = push(ref(this.db)).key;
            if (!key) throw new Error('Kunde inte skapa nyckel för golvets yta');
            const shape: ComplexShape = { key, geometryType: 'complex', area: floor.newArea };
            const areaShapes: ValueWithMetadata<AnyGeometryShape[]> = {
                value: [shape],
                updatedBy: uid,
                updatedWhen: now,
                status: ValueStatus.set,
            };
            updates[`buildings/${buildingKey}/structure/components/${floor.componentKey}/areaShapes`] = areaShapes;
        }

        const verificationPath = `buildings/${buildingKey}/structure/rooms/${roomKey}/floorAreaVerification`;
        if (input.verify) {
            const verification: FloorAreaVerification = { verifiedBy: uid, verifiedWhen: now };
            const comment = input.comment?.trim();
            if (comment) verification.comment = comment;
            updates[verificationPath] = verification;
        } else {
            updates[verificationPath] = null;
        }
        return updates;
    }

    async save(buildingKey: string, roomKey: string, input: SaveRoomFloorAreaInput): Promise<void> {
        const uid = getAuth().currentUser?.uid;
        if (!uid) throw new Error('Du måste vara inloggad för att ändra golvytan');
        if (!buildingKey || !roomKey) throw new Error('Rummet kunde inte hittas');

        const updates = this.buildUpdates(buildingKey, roomKey, input, uid);
        try {
            await update(ref(this.db), updates);
        } catch (err) {
            console.error('Error saving room floor area:', err);
            throw new Error('Kunde inte spara golvytan. Försök igen.');
        }
        recordActivity('room.updated', { buildingKey, targetKey: roomKey });
    }
}

export const roomFloorAreaApi = new RoomFloorAreaApi();
